/*
 * ELTANE — sticky add to cart
 *
 * Deliberately does NOT duplicate the product form. It observes the real buy
 * buttons and proxies to them, so variant selection, selling plans, quantity
 * rules and app-injected buttons all stay authoritative.
 */
if (!customElements.get('eltane-sticky-atc')) {
  customElements.define(
    'eltane-sticky-atc',
    class EltaneStickyAtc extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-eltane-sticky-submit]');
        this.buyNowButton = this.querySelector('[data-eltane-sticky-buy-now]');
        this.notifyButton = this.querySelector('[data-eltane-sticky-notify]');
        this.priceTarget = this.querySelector('[data-eltane-sticky-price]');
        this.anchor = document.querySelector('.product-form__buttons');
        this.sourcePrice = document.querySelector('.product__info-container .price');

        if (!this.anchor) {
          this.hidden = true;
          return;
        }

        this.observeAnchor();
        this.mirrorPrice();

        if (this.button) {
          this.button.addEventListener('click', this.onClick.bind(this));
        }
        if (this.buyNowButton) {
          this.buyNowButton.addEventListener('click', this.onBuyNow.bind(this));
          if (!this.getBuyNowButton()) this.buyNowButton.hidden = true;
        }
        if (this.notifyButton) {
          this.notifyButton.addEventListener('click', this.onNotify.bind(this));
        }
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.mutationObserver?.disconnect();
      }

      observeAnchor() {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            // Show the bar only once the real buy buttons have left the viewport.
            const anchorVisible = entries[0].isIntersecting;
            this.classList.toggle('is-visible', !anchorVisible);
          },
          { rootMargin: '0px 0px -40px 0px' }
        );

        this.intersectionObserver.observe(this.anchor);
      }

      mirrorPrice() {
        if (!this.sourcePrice || !this.priceTarget) return;

        const copy = () => {
          // Shopify keeps accessibility labels ("Regular price", "Sale price")
          // and a hidden sale block inside .price. Copy only the visible amount.
          const currentAmount = this.sourcePrice.querySelector(
            '.price-item--sale.price-item--last, .price__regular .price-item--regular:not(s)'
          );
          this.priceTarget.textContent = (currentAmount?.textContent || this.sourcePrice.textContent)
            .replace(/\s+/g, ' ')
            .trim();
        };

        copy();

        // Dawn re-renders the price node on variant change.
        this.mutationObserver = new MutationObserver(copy);
        this.mutationObserver.observe(this.sourcePrice, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      onClick(event) {
        event.preventDefault();

        const submit = this.anchor.querySelector('.product-form__submit');

        if (submit && !submit.disabled && submit.getAttribute('aria-disabled') !== 'true') {
          submit.click();
          return;
        }

        // Sold out, or a variant still needs choosing — send the shopper to the form.
        this.anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
        submit?.focus({ preventScroll: true });
      }

      getBuyNowButton() {
        return this.anchor?.querySelector('.shopify-payment-button__button:not(.shopify-payment-button__button--hidden)');
      }

      onBuyNow(event) {
        event.preventDefault();
        const buyNow = this.getBuyNowButton();
        if (buyNow && !buyNow.disabled) { buyNow.click(); return; }
        this.anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      onNotify(event) {
        event.preventDefault();
        this.anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Same proxy pattern as onClick/onBuyNow: no email field duplicated
        // here, this just opens the real "Notify me" form and hands off to it.
        const openButton = this.anchor.querySelector('[data-notify-open]');
        if (openButton && !openButton.hidden) {
          openButton.click();
          return;
        }
        this.anchor.querySelector('[data-notify-email]')?.focus({ preventScroll: true });
      }
    }
  );
}
